import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, MapPin, ArrowRight, Check, ChevronDown } from "lucide-react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { PHONES } from "../../content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ApplicationForm({ t }) {
  const [form, setForm] = useState({
    full_name: "",
    mobile: "",
    email: "",
    city: "",
    state: "",
    investment_capacity: "",
    business_profile: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.investment_capacity) {
      toast.error(t.error_title, { description: "Please select an investment capacity." });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/franchise/enquiry`, form);
      setSubmitted(true);
      toast.success(t.success_title, { description: t.success_body });
      setForm({ full_name: "", mobile: "", email: "", city: "", state: "", investment_capacity: "", business_profile: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(t.error_title, {
        description: typeof detail === "string" ? detail : t.error_body,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="apply" className="relative py-24 md:py-32 bg-stone-base" data-testid="application-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 lg:gap-16">
        {/* Left contact panel */}
        <div className="lg:col-span-5">
          <div className="text-amber-500 text-xs uppercase tracking-[0.3em] mb-4">{t.eyebrow}</div>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-stone-50 leading-tight">{t.title}</h2>
          <p className="mt-5 text-stone-300 text-base md:text-lg leading-relaxed">{t.sub}</p>

          <div className="mt-12">
            <div className="text-stone-400 text-xs uppercase tracking-[0.3em] mb-4">{t.contact_title}</div>
            <div className="space-y-3">
              {PHONES.map((p) => (
                <a
                  key={p}
                  href={`tel:${p}`}
                  data-testid={`phone-link-${p}`}
                  className="flex items-center gap-4 p-4 heritage-card hover:border-amber-600/50 group"
                >
                  <div className="w-11 h-11 rounded-xl bg-amber-600/10 border border-amber-600/30 flex items-center justify-center text-amber-500 group-hover:bg-amber-600 group-hover:text-stone-950 transition-colors">
                    <Phone size={18} />
                  </div>
                  <div>
                    <div className="font-serif text-2xl text-stone-50 tracking-wide">+91 {p}</div>
                    <div className="text-stone-500 text-xs uppercase tracking-wider">Partnership Desk</div>
                  </div>
                </a>
              ))}
            </div>

            <div className="mt-6 flex items-start gap-4 p-4 heritage-card">
              <div className="w-11 h-11 rounded-xl bg-stone-950 border border-stone-700 flex items-center justify-center text-stone-300">
                <MapPin size={18} />
              </div>
              <div>
                <div className="text-stone-200">In front of Krishna Pranami Mandir</div>
                <div className="text-stone-500 text-sm">Meerapur, Prayagraj — 211003, UP, India</div>
              </div>
            </div>
          </div>
        </div>

        {/* Right form */}
        <div className="lg:col-span-7">
          <div className="heritage-card p-7 md:p-10">
            {submitted ? (
              <div className="text-center py-10" data-testid="form-success-state">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-600 text-stone-950 mb-6">
                  <Check size={28} strokeWidth={2.5} />
                </div>
                <h3 className="font-serif text-3xl text-stone-50">{t.success_title}</h3>
                <p className="mt-3 text-stone-300 max-w-md mx-auto">{t.success_body}</p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="mt-8 text-amber-500 hover:text-amber-400 text-sm uppercase tracking-wider"
                  data-testid="form-reset-btn"
                >
                  Submit another →
                </button>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-5" data-testid="franchise-form">
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field id="full_name" label={t.labels.name}>
                    <Input
                      id="full_name"
                      data-testid="form-input-name"
                      value={form.full_name}
                      onChange={(e) => update("full_name", e.target.value)}
                      placeholder={t.placeholders.name}
                      required
                      className="bg-stone-950 border-stone-700 text-stone-100 focus-visible:ring-amber-600 focus-visible:border-amber-600 h-11"
                    />
                  </Field>
                  <Field id="mobile" label={t.labels.mobile}>
                    <Input
                      id="mobile"
                      data-testid="form-input-mobile"
                      value={form.mobile}
                      onChange={(e) => update("mobile", e.target.value.replace(/[^\d+\s-]/g, ""))}
                      placeholder={t.placeholders.mobile}
                      required
                      type="tel"
                      className="bg-stone-950 border-stone-700 text-stone-100 focus-visible:ring-amber-600 focus-visible:border-amber-600 h-11"
                    />
                  </Field>
                </div>

                <Field id="email" label={t.labels.email}>
                  <Input
                    id="email"
                    data-testid="form-input-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    placeholder={t.placeholders.email}
                    required
                    className="bg-stone-950 border-stone-700 text-stone-100 focus-visible:ring-amber-600 focus-visible:border-amber-600 h-11"
                  />
                </Field>

                <div className="grid sm:grid-cols-2 gap-5">
                  <Field id="city" label={t.labels.city}>
                    <Input
                      id="city"
                      data-testid="form-input-city"
                      value={form.city}
                      onChange={(e) => update("city", e.target.value)}
                      placeholder={t.placeholders.city}
                      required
                      className="bg-stone-950 border-stone-700 text-stone-100 focus-visible:ring-amber-600 focus-visible:border-amber-600 h-11"
                    />
                  </Field>
                  <Field id="state" label={t.labels.state}>
                    <Input
                      id="state"
                      data-testid="form-input-state"
                      value={form.state}
                      onChange={(e) => update("state", e.target.value)}
                      placeholder={t.placeholders.state}
                      required
                      className="bg-stone-950 border-stone-700 text-stone-100 focus-visible:ring-amber-600 focus-visible:border-amber-600 h-11"
                    />
                  </Field>
                </div>

                <Field id="capacity" label={t.labels.capacity}>
                  <div className="relative">
                    <select
                      id="capacity"
                      data-testid="form-select-capacity"
                      value={form.investment_capacity}
                      onChange={(e) => update("investment_capacity", e.target.value)}
                      required
                      className="appearance-none w-full h-11 px-3 pr-10 rounded-md bg-stone-950 border border-stone-700 text-stone-100 text-sm focus:outline-none focus:ring-2 focus:ring-amber-600 focus:border-amber-600 cursor-pointer"
                    >
                      <option value="" disabled>Select your investment range…</option>
                      {t.capacity_options.map((opt) => (
                        <option key={opt} value={opt} className="bg-stone-900 text-stone-100">
                          {opt}
                        </option>
                      ))}
                    </select>
                    <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-500 pointer-events-none" />
                  </div>
                </Field>

                <Field id="profile" label={t.labels.profile}>
                  <Textarea
                    id="profile"
                    data-testid="form-input-profile"
                    value={form.business_profile}
                    onChange={(e) => update("business_profile", e.target.value)}
                    placeholder={t.placeholders.profile}
                    rows={4}
                    className="bg-stone-950 border-stone-700 text-stone-100 focus-visible:ring-amber-600 focus-visible:border-amber-600 resize-none"
                  />
                </Field>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="form-submit-btn"
                  className="w-full inline-flex items-center justify-center gap-2 bg-amber-600 hover:bg-amber-700 disabled:opacity-60 disabled:cursor-not-allowed text-stone-950 font-medium uppercase tracking-wider text-sm px-7 py-4 rounded-full btn-primary-glow mt-2"
                >
                  {submitting ? t.submitting : t.submit}
                  {!submitting && <ArrowRight size={16} />}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ id, label, children }) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id} className="text-stone-300 text-xs uppercase tracking-[0.2em]">{label}</Label>
      {children}
    </div>
  );
}
